import React, { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { Button, Modal, Form } from 'react-bootstrap';
import { DateTime } from 'luxon';
import { Alert } from 'react-bootstrap';
import { isEqual } from 'lodash';

import { useAppSelector } from 'hooks';
import { DateTimePicker } from 'components/UI';
import { ExitPopupLightIcon } from 'assets/icons';
import { useAxios } from 'configs/requests';
import { Error, Loading } from 'components/Fetch';
import { isValidUrl } from 'utils/Helpers/Url';

import RecurrenceEngine from '../../../RecurrenceEngine';

interface IAddEditEducationalContentModalProps {
  show: boolean;
  handleClose: any;
  handleEdit: any;
  educationalContent?: ICarePlanEducationalContent;
  carePlanTemplate?: ICarePlanTemplate;
}

const AddEditEducationalContentModal = ({
  show,
  handleClose,
  handleEdit,
  educationalContent,
  carePlanTemplate
}: IAddEditEducationalContentModalProps) => {
  const intl = useIntl();
  const patientUid = useAppSelector(({ patient }) => patient.data?.uid);
  const locale = useAppSelector((state) => state.intl.locale);
  const [articles, setArticles] = useState(
    educationalContent?.articles || [{ title: '', url: '' }]
  );
  const [recurrence, setRecurrence] = useState(
    educationalContent?.isRepeating ? educationalContent?.recurrence : ''
  );

  const [validRecurrence, setValidRecurrence] = useState(true);
  const [editButtonDisabled, setEditButtonDisabled] = useState(false);

  const now = DateTime.now().setLocale(locale);
  const educationalContentDate =
    educationalContent?.send_time && typeof educationalContent?.send_time !== 'number'
      ? DateTime.fromFormat(educationalContent.send_time, 'yyyy-MM-dd HH:mm:ss')
      : undefined;
  const [date, setDate] = useState<DateTime>(educationalContentDate || now.plus({ hours: 1 }));

  const [{ loading, error: postError }, post] = useAxios(
    {
      url: carePlanTemplate
        ? '/clinician/2.0.0/CarePlanTemplate'
        : `/clinician/Patient/${patientUid}/CarePlan`,
      method: 'POST'
    },
    { manual: true }
  );

  const _handleEdit = async () => {
    const educationalContentToUpdate = {
      edit_status: educationalContent ? 'updated' : 'new',
      id: educationalContent?.id,
      action: {
        articles,
        message_type: '14',
        localDateTime: date.toFormat('yyyy-MM-dd HH:mm')
      },
      recurrence
    };

    await post({
      data: {
        scheduled: [educationalContentToUpdate],
        carePlanTemplate
      }
    });

    handleEdit();
  };

  const handleChangeDate = (date: DateTime) => {
    setDate(date);
  };

  const onChangeRecurrence = (_recurrence: string, _valid: boolean) => {
    setRecurrence(_recurrence);
    setValidRecurrence(_valid);
  };

  const handleChangeTitle = (index: number, value: string) => {
    const newArticles = articles.map((article, key) => {
      if (key === index) {
        article.title = value;
      }
      return article;
    });
    setArticles(newArticles);
  };

  const handleChangeUrl = (index: number, value: string) => {
    const newArticles = articles.map((article, key) => {
      if (key === index) {
        article.url = value;
      }
      return article;
    });
    setArticles(newArticles);
  };

  const handleAddUrl = () => {
    setArticles([...articles, { title: '', url: '' }]);
  };

  const handleDeleteUrl = (index: number) => {
    setArticles(articles.filter((e, key) => key !== index));
  };

  useEffect(() => {
    const isEducationalContentIdentical =
      isEqual(articles, educationalContent?.articles) &&
      recurrence === (educationalContent?.isRepeating ? educationalContent.recurrence : '') &&
      date.toMillis() === educationalContentDate?.toMillis();
    const invalidArticle =
      !articles.length || !!articles.find((article) => !article.title || !isValidUrl(article.url));
    setEditButtonDisabled(invalidArticle || !validRecurrence || isEducationalContentIdentical);
  }, [articles, validRecurrence, recurrence, educationalContent, date, educationalContentDate]);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      data-test="careplan-educational-contents-modal"
    >
      <Modal.Header className="align-items-center" closeButton>
        <Modal.Title>{`${intl.formatMessage({
          id: educationalContent ? 'editKey' : 'add'
        })} ${intl.formatMessage({
          id: 'educationalContentKey'
        })}`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="primary" data-test="careplan-educational-contents-modal-information">
          {intl.formatMessage({ id: 'educationalContentInformationKey' })}
        </Alert>
        <div className="d-flex align-items-center justify-content-between">
          <Form.Label className="m-0">{intl.formatMessage({ id: 'articlesKey' })}</Form.Label>
          <Button
            variant="link"
            size="sm"
            onClick={handleAddUrl}
            data-test="educational-contents-add"
          >
            + {intl.formatMessage({ id: 'add' })}
          </Button>
        </div>
        {articles.map((article, index) => (
          <div key={index} data-test={`educational-content-${index}`}>
            <div className="d-flex align-items-center">
              <Form.Control
                name="title"
                value={article.title}
                onChange={(e) => handleChangeTitle(index, e.target.value)}
                className="mb-1 me-1 w-40"
                placeholder={intl.formatMessage({ id: 'titleKey' })}
                data-test={`educational-contents-${index}-title`}
              />
              <Form.Control
                name="url"
                value={article.url}
                onChange={(e) => handleChangeUrl(index, e.target.value)}
                placeholder={intl.formatMessage({ id: 'urlKey' })}
                className="mb-1"
                data-test={`educational-contents-${index}-url`}
              />
              <Button variant="link p-1 m-2" size="sm" onClick={() => handleDeleteUrl(index)}>
                <ExitPopupLightIcon className="icon" />
              </Button>
            </div>
            <hr className="m-1" />
          </div>
        ))}

        <div className="d-flex flex-column mt-4">
          <Form.Label>
            {intl.formatMessage({ id: 'dateKey' }) + ' & ' + intl.formatMessage({ id: 'timeKey' })}
          </Form.Label>
          <DateTimePicker
            date={date}
            todayLimit="min"
            onChangeDate={handleChangeDate}
            minDate={now}
            containerClassName="mt-3"
          />
        </div>

        <RecurrenceEngine
          recurrence={recurrence}
          isActive={educationalContent?.isRepeating || false}
          onChange={onChangeRecurrence}
          date={date}
        />

        {loading && <Loading className="mt-4" />}
        {postError && <Error className="mt-4" retry={_handleEdit} dismissible={false} />}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="link"
          onClick={handleClose}
          data-test="careplan-educational-contents-modal-close-btn"
        >
          {intl.formatMessage({ id: 'closeKey' })}
        </Button>
        <Button
          variant="primary"
          onClick={_handleEdit}
          disabled={editButtonDisabled}
          data-test="careplan-educational-contents-modal-save-btn"
        >
          {intl.formatMessage({ id: educationalContent ? 'saveButtonTitle' : 'add' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddEditEducationalContentModal;
